import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCampaignPreviewCounts } from '@euler/model/apdm';
import { ChevronRight, LucideIcon } from 'lucide-react-native';
import { ReactNode, memo, useMemo } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { aiColors, tint } from '../theme';

/** A rich card in the conversation: header, body, and a way to the details. */
export const ChatCard = memo(
  ({
    icon: Icon,
    color = aiColors.primary,
    title,
    subtitle,
    badge,
    tinted,
    detail,
    children,
  }: {
    icon: LucideIcon;
    color?: string;
    title: string;
    subtitle?: string | null;
    badge?: ReactNode;
    /** A colored header band, for the card a reply is built around. */
    tinted?: boolean;
    detail?: { label: string; onPress: () => void } | null;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useChatCardStyles();
    return (
      <View style={styles.card}>
        <View
          style={[
            styles.header,
            tinted && { backgroundColor: tint(color, 0.1) },
          ]}
        >
          <View
            style={[
              styles.icon,
              {
                backgroundColor: tinted ? color : tint(color, 0.15),
              },
            ]}
          >
            <Icon size={18} color={tinted ? '#fff' : color} />
          </View>
          <View style={styles.titles}>
            <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
              {title}
            </Typography.Text>
            {subtitle ? (
              <Typography.Text
                size={rpx(11)}
                color={aiColors.mutedForeground}
                numberOfLines={2}
              >
                {subtitle}
              </Typography.Text>
            ) : null}
          </View>
          {badge}
        </View>
        {children ? (
          <View style={[styles.body, tinted && styles.bodyBelowBand]}>
            {children}
          </View>
        ) : null}
        {detail ? (
          <Pressable
            accessibilityRole="link"
            onPress={detail.onPress}
            style={({ pressed }) => [
              styles.detail,
              pressed && { opacity: 0.7 },
            ]}
          >
            <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
              {detail.label}
            </Typography.Text>
            <ChevronRight size={14} color={aiColors.primary} />
          </Pressable>
        ) : null}
      </View>
    );
  },
);

export const ChatCardBadge = memo(
  ({ label, color = aiColors.primary }: { label: string; color?: string }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(2),
          borderRadius: rpx(10),
          backgroundColor: tint(color, 0.12),
        }}
      >
        <Typography.Text medium size={rpx(10)} color={color}>
          {label}
        </Typography.Text>
      </View>
    );
  },
);

/** A big number over its label, as in the import and audience tiles. */
export const StatTile = memo(
  ({
    value,
    label,
    color = aiColors.foreground,
    tone,
  }: {
    value: number | string;
    label: string;
    color?: string;
    tone?: 'danger';
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          paddingVertical: rpx(8),
          paddingHorizontal: rpx(4),
          borderRadius: rpx(10),
          backgroundColor:
            tone === 'danger'
              ? tint(aiColors.destructive, 0.1)
              : aiColors.muted,
        }}
      >
        <Typography.Text
          bold
          size={rpx(16)}
          color={tone === 'danger' ? aiColors.destructive : color}
        >
          {typeof value === 'number' ? value.toLocaleString('zh-CN') : value}
        </Typography.Text>
        <Typography.Text size={rpx(10)} color={aiColors.mutedForeground}>
          {label}
        </Typography.Text>
      </View>
    );
  },
);

/**
 * Who a campaign reaches: everyone it may contact, how many of them have a
 * confirmed contact, and who it leaves out. Unconfirmed customers are
 * still reached, so they count as reachable.
 */
export const AudienceCounts = memo(
  ({ counts }: { counts: ApdmCampaignPreviewCounts }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ flexDirection: 'row', gap: rpx(8) }}>
        <StatTile
          value={counts.known + counts.unconfirmed}
          label="可触达"
          color={aiColors.green}
        />
        <StatTile
          value={counts.known}
          label="已确认"
          color={aiColors.primary}
        />
        <StatTile
          value={counts.excluded}
          label="已排除"
          color={aiColors.mutedForeground}
        />
      </View>
    );
  },
);

export const CardLoading = memo(({ label }: { label: string }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: rpx(8),
        paddingVertical: rpx(6),
      }}
    >
      <ActivityIndicator size="small" color={aiColors.primary} />
      <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
        {label}
      </Typography.Text>
    </View>
  );
});

export const CardNotice = memo(
  ({
    text,
    tone = 'muted',
    center,
  }: {
    text: string;
    tone?: 'muted' | 'danger';
    center?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Typography.Text
        accessibilityRole={tone === 'danger' ? 'alert' : undefined}
        size={rpx(12)}
        lineHeight={rpx(18)}
        align={center ? 'center' : undefined}
        color={
          tone === 'danger' ? aiColors.destructive : aiColors.mutedForeground
        }
      >
        {text}
      </Typography.Text>
    );
  },
);

export function useChatCardStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: aiColors.card,
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          overflow: 'hidden',
          marginVertical: rpx(6),
          shadowColor: '#0F172A',
          shadowOpacity: 0.04,
          shadowRadius: 4,
          shadowOffset: { width: 0, height: 1 },
          elevation: 1,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(10),
          padding: rpx(14),
        },
        icon: {
          width: rpx(36),
          height: rpx(36),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
        },
        titles: { flex: 1, gap: rpx(2) },
        body: {
          paddingHorizontal: rpx(14),
          paddingBottom: rpx(14),
          gap: rpx(12),
        },
        bodyBelowBand: { paddingTop: rpx(14) },
        detail: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(2),
          paddingVertical: rpx(10),
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: aiColors.border,
        },
      }),
    [rpx],
  );
}
